import React, { useEffect, useMemo, useState } from "react";
import { getCatalogEntry, getCategoryLabel, ITEM_CATEGORIES } from "@/data/vipSimulatorCatalog";
import { isItensDeArmasCatalogEntry } from "@/data/weaponAttachments";
import { useI18n } from "@/i18n/I18nProvider";
import ItemCard from "./ItemCard";

export default function AvailableItems({ pool, onQuantityChange, onAddToKit }) {
    const { t, lang } = useI18n();
    const categoriesWithStock = useMemo(
        () => ITEM_CATEGORIES.filter((c) => (pool[c.key] || []).length > 0),
        [pool],
    );

    const anyInPool = categoriesWithStock.length > 0;

    const defaultCategoryKey = categoriesWithStock[0]?.key ?? "todos";
    const [filterKey, setFilterKey] = useState(defaultCategoryKey);

    useEffect(() => {
        if (filterKey === "todos" || categoriesWithStock.some((c) => c.key === filterKey)) return;
        setFilterKey(categoriesWithStock[0]?.key ?? "todos");
    }, [categoriesWithStock, filterKey]);

    const chips = useMemo(() => {
        const base = [{ key: "todos", label: t("simulator.pool.all") }];
        return base.concat(categoriesWithStock.map((c) => ({ key: c.key, label: getCategoryLabel(c.key, lang) })));
    }, [categoriesWithStock, t, lang]);

    const renderCategoryBlock = (catKey, label) => {
        const list = (pool[catKey] || []).filter((placement) => {
            const entry = getCatalogEntry(placement.catalogId);
            if (catKey === "Armas" && entry?.cat === "attachment" && isItensDeArmasCatalogEntry(entry)) {
                return false;
            }
            return true;
        });
        if (!list.length) return null;
        return (
            <div key={catKey} className="vip-sim-pool-section">
                <h3 className="vip-sim-pool-section__title">{label}</h3>
                <div className="vip-sim-pool-grid">
                    {list.map((placement) => (
                        <ItemCard
                            key={placement.instanceId}
                            placement={placement}
                            inPool
                            onQuantityChange={onQuantityChange}
                            onAddToKit={onAddToKit}
                        />
                    ))}
                </div>
            </div>
        );
    };

    return (
        <div className="vip-sim-available">
            <div className="vip-sim-available__head">
                <h3 className="vip-sim-available__title">{t("simulator.pool.title")}</h3>
                <p className="vip-sim-available__hint">{t("simulator.pool.hint")}</p>
            </div>
            <div className="vip-sim-cat-scroll" role="tablist" aria-label={t("simulator.pool.catsAria")}>
                {chips.map((chip) => (
                    <button
                        key={chip.key}
                        type="button"
                        role="tab"
                        aria-selected={filterKey === chip.key}
                        className={`vip-sim-cat-chip ${filterKey === chip.key ? "vip-sim-cat-chip--active" : ""}`}
                        onClick={() => setFilterKey(chip.key)}
                    >
                        {chip.label}
                    </button>
                ))}
            </div>
            <div className="vip-sim-pool-scroll">
                {!anyInPool ? (
                    <p className="vip-sim-pool-empty">{t("simulator.pool.emptyCatalog")}</p>
                ) : filterKey !== "todos" && !(pool[filterKey] || []).length ? (
                    <p className="vip-sim-pool-empty">{t("simulator.pool.emptyCategory")}</p>
                ) : filterKey === "todos" ? (
                    <div className="vip-sim-pool-stack">
                        {ITEM_CATEGORIES.map((c) => renderCategoryBlock(c.key, getCategoryLabel(c.key, lang)))}
                    </div>
                ) : (
                    <div className="vip-sim-pool-stack">
                        {renderCategoryBlock(filterKey, getCategoryLabel(filterKey, lang))}
                    </div>
                )}
            </div>
        </div>
    );
}
